import { useCallback, useRef, type MouseEvent, type TouchEvent } from "react";

const DEFAULT_THRESHOLD_PX = 50;

export interface UseHorizontalSwipeOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  enabled?: boolean;
  thresholdPx?: number;
}

/**
 * Touch swipe left/right handlers for mobile & tablet card navigation.
 * Distinguishes horizontal swipes from vertical scroll; suppresses the
 * synthetic click that often follows a swipe so taps (e.g. flip) still work.
 */
export function useHorizontalSwipe({
  onSwipeLeft,
  onSwipeRight,
  enabled = true,
  thresholdPx = DEFAULT_THRESHOLD_PX,
}: UseHorizontalSwipeOptions) {
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const didSwipeRef = useRef(false);

  const onTouchStart = useCallback(
    (event: TouchEvent) => {
      if (!enabled) return;
      const touch = event.touches[0];
      if (!touch) return;
      startRef.current = { x: touch.clientX, y: touch.clientY };
      didSwipeRef.current = false;
    },
    [enabled],
  );

  const onTouchEnd = useCallback(
    (event: TouchEvent) => {
      if (!enabled || !startRef.current) return;
      const touch = event.changedTouches[0];
      if (!touch) {
        startRef.current = null;
        return;
      }

      const dx = touch.clientX - startRef.current.x;
      const dy = touch.clientY - startRef.current.y;
      startRef.current = null;

      // Prefer vertical scroll over horizontal swipe when ambiguous.
      if (Math.abs(dx) < thresholdPx || Math.abs(dx) <= Math.abs(dy)) {
        return;
      }

      didSwipeRef.current = true;
      if (dx > 0) {
        onSwipeRight?.();
      } else {
        onSwipeLeft?.();
      }
    },
    [enabled, onSwipeLeft, onSwipeRight, thresholdPx],
  );

  const onClickCapture = useCallback((event: MouseEvent) => {
    if (!didSwipeRef.current) return;
    didSwipeRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  return {
    onTouchStart,
    onTouchEnd,
    onClickCapture,
  };
}
